/*
 * Kubernetes Enhanced Plugin Types
 */

/**
 * Kubernetes 资源状态
 */
export type ResourceStatus = 'Running' | 'Pending' | 'Failed' | 'Succeeded' | 'Unknown';

/**
 * Pod 状态
 */
export interface PodStatus {
  name: string;
  namespace: string;
  status: ResourceStatus;
  ready: string; // e.g., "1/1"
  restarts: number;
  age: string;
  node: string;
  ip?: string;
  containers: ContainerStatus[];
}

/**
 * Container 状态
 */
export interface ContainerStatus {
  name: string;
  image: string;
  ready: boolean;
  restartCount: number;
  state: 'running' | 'waiting' | 'terminated';
  reason?: string;
}

/**
 * Deployment 状态
 */
export interface DeploymentStatus {
  name: string;
  namespace: string;
  replicas: number;
  readyReplicas: number;
  availableReplicas: number;
  updatedReplicas: number;
  strategy: string;
  image: string;
  age: string;
  conditions: DeploymentCondition[];
}

/**
 * Deployment Condition
 */
export interface DeploymentCondition {
  type: string;
  status: 'True' | 'False' | 'Unknown';
  reason?: string;
  message?: string;
  lastUpdateTime?: string;
}

/**
 * Service 信息
 */
export interface ServiceInfo {
  name: string;
  namespace: string;
  type: 'ClusterIP' | 'NodePort' | 'LoadBalancer' | 'ExternalName';
  clusterIP: string;
  externalIP?: string;
  ports: ServicePort[];
  age: string;
}

/**
 * Service Port
 */
export interface ServicePort {
  name?: string;
  port: number;
  targetPort: number | string;
  protocol: string;
  nodePort?: number;
}

/**
 * Ingress 信息
 */
export interface IngressInfo {
  name: string;
  namespace: string;
  hosts: string[];
  address?: string;
  age: string;
}

/**
 * Event 信息
 */
export interface EventInfo {
  type: 'Normal' | 'Warning';
  reason: string;
  message: string;
  involvedObject: {
    kind: string;
    name: string;
  };
  count: number;
  firstTimestamp: string;
  lastTimestamp: string;
  age: string;
}

/**
 * 资源使用指标
 */
export interface ResourceMetrics {
  cpu: {
    usage: number;      // millicores
    requests: number;   // millicores
    limits: number;     // millicores
    percentage: number; // 0-100
  };
  memory: {
    usage: number;      // bytes
    requests: number;   // bytes
    limits: number;     // bytes
    percentage: number; // 0-100
  };
}

/**
 * 资源概览统计
 */
export interface ResourceOverview {
  pods: {
    total: number;
    running: number;
    pending: number;
    failed: number;
  };
  services: number;
  ingresses: number;
  configMaps: number;
  secrets: number;
}

/**
 * 集群信息
 */
export interface ClusterInfo {
  name: string;
  namespace: string;
  workloadName: string;
  kubernetesId?: string;
  poweredBy?: 'knative' | 'argocd' | 'kubernetes';
  knativeServiceName?: string;
}

/**
 * Kubernetes 注解常量
 */
export const KUBERNETES_ANNOTATIONS = {
  CLUSTER_NAME: 'backstage.io/kubernetes-cluster-name',
  NAMESPACE: 'backstage.io/kubernetes-namespace',
  ID: 'backstage.io/kubernetes-id',
  LABEL_SELECTOR: 'backstage.io/kubernetes-label-selector',
  POWERED_BY: 'hose.io/powered-by',
  KNATIVE_SERVICE_NAME: 'knative.dev/service-name',
  KNATIVE_SERVICE_NAMESPACE: 'knative.dev/service-namespace',
} as const;

// ==================== Knative Types ====================

/**
 * Knative Service 状态
 */
export interface KnativeServiceStatus {
  name: string;
  namespace: string;
  url?: string;
  latestReadyRevision?: string;
  latestCreatedRevision?: string;
  traffic: KnativeTrafficTarget[];
  conditions: KnativeCondition[];
  age: string;
  generation: number;
  observedGeneration?: number;
}

/**
 * Knative Traffic Target
 */
export interface KnativeTrafficTarget {
  revisionName?: string;
  latestRevision?: boolean;
  percent: number;
  tag?: string;
  url?: string;
}

/**
 * Knative Condition
 */
export interface KnativeCondition {
  type: string;
  status: 'True' | 'False' | 'Unknown';
  reason?: string;
  message?: string;
  lastTransitionTime?: string;
}

/**
 * Knative Revision 状态
 */
export interface KnativeRevisionStatus {
  name: string;
  namespace: string;
  serviceName: string;
  generation: number;
  image: string;
  replicas: number;
  readyReplicas: number;
  conditions: KnativeCondition[];
  age: string;
  trafficPercent?: number;
  isLatest: boolean;
}

/**
 * Knative Route 状态
 */
export interface KnativeRouteStatus {
  name: string;
  namespace: string;
  url?: string;
  traffic: KnativeTrafficTarget[];
  conditions: KnativeCondition[];
  age: string;
}

/**
 * Knative Configuration 状态
 */
export interface KnativeConfigurationStatus {
  name: string;
  namespace: string;
  latestReadyRevision?: string;
  latestCreatedRevision?: string;
  conditions: KnativeCondition[];
  age: string;
}

/**
 * Knative 资源概览
 */
export interface KnativeResourceOverview {
  service: {
    name: string;
    ready: boolean;
    url?: string;
  };
  revisions: {
    total: number;
    ready: number;
    active: number; // 有流量的
  };
  pods: {
    total: number;
    running: number;
    pending: number;
    failed: number;
  };
  traffic: KnativeTrafficTarget[];
}

